import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, Bars3Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PORTALS } from './portals';

const LINKS = [
  { name: 'Home', to: '/' },
  { name: 'Solutions', to: '/services' },
  { name: 'Coverage', to: '/global-coverage' },
  { name: 'Why TrueLog', to: '/why-truelog' },
  { name: 'Contact', to: '/contact-us' },
];

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

// Transparent overlay nav for the cinematic hero (turns to glass once scrolled).
const LandingNav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const { pathname } = useLocation();

  // close the Client login menu on outside click / Escape
  useEffect(() => {
    if (!loginOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-login-menu]')) setLoginOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLoginOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [loginOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div
        className={`lp-nav-bar ${scrolled || open ? 'is-scrolled' : ''} transition-all duration-500 ${
          scrolled || open
            ? 'border-b border-white/10 bg-[#02040a]/75 backdrop-blur-xl'
            : 'border-b border-transparent bg-transparent'
        }`}
      >
        <nav className="mx-auto flex h-[76px] max-w-[1480px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" className="flex shrink-0 items-center" aria-label="TrueLog home">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/truelog-logo-horizontal.png`}
              alt="TrueLog"
              className="h-9 w-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]"
            />
          </Link>

          <div className="hidden items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.04] p-1 backdrop-blur-md md:flex">
            {LINKS.map((link) => {
              const active = pathname === link.to;
              return (
                <Link
                  key={link.name}
                  to={link.to}
                  className={`relative rounded-full px-4 py-1.5 text-[13.5px] font-medium transition-colors ${
                    active ? 'text-white' : 'text-white/60 hover:text-white'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="lp-nav-pill"
                      className="absolute inset-0 rounded-full bg-white/10"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span className="relative">{link.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden lg:block" data-login-menu>
              <button
                type="button"
                onClick={() => setLoginOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={loginOpen}
                className={`inline-flex items-center gap-1 px-3 py-2 text-[13px] font-medium transition-colors ${
                  loginOpen ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                Client login
                <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${loginOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {loginOpen && (
                  <motion.div
                    role="menu"
                    initial={{ opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.98 }}
                    transition={{ duration: 0.18, ease: EASE }}
                    className="lp-login-menu absolute right-0 top-full mt-2 w-[300px] overflow-hidden rounded-2xl border border-white/12 bg-[#070b16]/95 p-1.5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl"
                  >
                    {PORTALS.map((portal) => (
                      <a
                        key={portal.label}
                        role="menuitem"
                        href={portal.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setLoginOpen(false)}
                        className="group flex items-start justify-between gap-3 rounded-xl px-3.5 py-3 transition-colors hover:bg-white/[0.07]"
                      >
                        <span>
                          <span className="block text-[13.5px] font-semibold text-white">{portal.label}</span>
                          <span className="mt-0.5 block text-[12px] leading-5 text-white/55">{portal.hint}</span>
                        </span>
                        <ArrowUpRightIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-white/40 transition-colors group-hover:text-white" />
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/contact-us" className="lp-btn-ghost hidden sm:inline-flex">
              Get started
            </Link>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="inline-flex rounded-full border border-white/15 bg-white/[0.06] p-2 text-white md:hidden"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lp-mobile-menu fixed inset-x-0 bottom-0 top-[76px] z-40 overflow-y-auto bg-[#02040a]/92 px-5 py-6 backdrop-blur-2xl md:hidden"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                >
                  <Link
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={`block rounded-2xl px-4 py-3.5 text-lg font-medium ${
                      pathname === link.to ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/[0.06] hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 border-t border-white/10 pt-6">
              <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">Client login</p>
              <div className="mt-3 flex flex-col gap-2">
                {PORTALS.map((portal) => (
                  <a
                    key={portal.label}
                    href={portal.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/[0.04] px-4 py-3 text-left"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-white">{portal.label}</span>
                      <span className="block text-xs text-white/55">{portal.hint}</span>
                    </span>
                    <ArrowUpRightIcon className="h-4 w-4 shrink-0 text-white/50" />
                  </a>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="rounded-2xl bg-white px-4 py-3 text-center text-sm font-semibold text-slate-900"
              >
                Get started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default LandingNav;
